import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";

import { Form, DropdownProps } from "semantic-ui-react";

import { EditComponent, SaveDispatchProps, EditProps as SharedEditProps } from "@neworbit/simpleui-forms";
import { HrRepresentative } from "@teamprevent/common/hrRepresentatives/models";
import { LineManager } from "@teamprevent/common/lineManagers/models";
import { CaseType } from "@teamprevent/common/model";

import { CurrentUser } from "../../../../auth";
import { ApplicationState } from "../../../../applicationState";
import { ohCaseSelector, basePathSelector } from "../../selectors";
import { loadHrRepresentativeValues, loadLineManagerValues, updateAdditionalManagers } from "../../actions";

import { ManageAssignedUsersModal } from "./ManageAssignedUsersModal";

import { push } from "redux-little-router";

export interface ManageAssignedUsersModel {
    associatedManagerIds?: string[];
    associatedHrIds?: string[];
    lineManagers: LineManager[];
    hrRepresentatives: HrRepresentative[];
    caseType?: string;
}

export interface ManageAssignedUsersProps extends SharedEditProps<ManageAssignedUsersModel> {
    open: boolean;
    currentUser: CurrentUser;
}

export interface DispatchProps extends SaveDispatchProps<ManageAssignedUsersModel> {
    close: () => void;
    getLineManagers: () => void;
    getHrRepresentatives: () => void;
}

export class ManageAssignedUsers extends EditComponent<ManageAssignedUsersModel, ManageAssignedUsersProps & DispatchProps> {

    public componentDidMount(): void {
        this.props.getLineManagers();
        this.props.getHrRepresentatives();
    }

    public render() {
        return (
            <Form>
                <Form.Select
                    search={true}
                    multiple
                    label="Associated Managers *"
                    options={(this.state.values.lineManagers || []).map(m => ({ key: m.id, value: m.id, text: m.email + ` (${m.name})` }))}
                    value={this.state.values.associatedManagerIds}
                    onChange={this.onLineManagersChange}
                />
                <Form.Select
                    search={true}
                    multiple
                    label="Associated HRs *"
                    options={(this.state.values.hrRepresentatives || []).map(m => ({ key: m.id, value: m.id, text: m.email + ` (${m.name})` }))}
                    value={this.state.values.associatedHrIds}
                    onChange={this.onHrRepresentativesChange}
                />
            </Form>
        );
    }

    public async submit() {
        await this.props.save(this.state.values);
    }

    private onLineManagersChange = (e: React.SyntheticEvent<HTMLElement, Event>, d: DropdownProps) =>
        this.setState({ values: { ...this.state.values, associatedManagerIds: d.value as string[] } });

    private onHrRepresentativesChange = (e: React.SyntheticEvent<HTMLElement, Event>, d: DropdownProps) =>
        this.setState({ values: { ...this.state.values, associatedHrIds: d.value as string[] } });
}

function mapStateToProps(state: ApplicationState) {
    return {
        model: ohCaseSelector(state) as ManageAssignedUsersModel,
        open: state.router.pathname.endsWith("/manageAssignedUsers"),
        basePath: basePathSelector(state),
        currentUser: state.currentUser
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, undefined, any>) {
    return {
        dispatchSave: (
            ohCaseId: string,
            model: ManageAssignedUsersModel,
            basePath: string,
            currentUserId: string) =>
            dispatch(updateAdditionalManagers(
                ohCaseId,
                model.associatedManagerIds.map(id => ({
                    id,
                    name: model.lineManagers.find(l => l.id === id).name as string
                })),
                model.associatedHrIds.map(id => ({
                    id,
                    name: model.hrRepresentatives.find(h => h.id === id).name as string
                })),
                basePath,
                currentUserId,
                model.caseType === CaseType.BBV || model.caseType === CaseType.IMMUNISATION)),
        dispatchClose: (basePath: string) => dispatch(push(basePath, {})),
        dispatchGetLineManagers: (ohCaseId: string) => dispatch(loadLineManagerValues({ ohCaseId })),
        dispatchGetHrRepresentatives: (ohCaseId: string) => dispatch(loadHrRepresentativeValues({ ohCaseId }))
    };
}

function mergeProps(propsFromState: any, propsFromDispatch: any): ManageAssignedUsersProps & DispatchProps {
    return {
        model: propsFromState.model,
        open: propsFromState.open,
        currentUser: propsFromState.currentUser,
        save: (model: ManageAssignedUsersModel) =>
            propsFromDispatch.dispatchSave(
                propsFromState.model.id,
                model,
                propsFromState.basePath,
                propsFromState.currentUser.id),
        close: () => propsFromDispatch.dispatchClose(`${propsFromState.basePath}/${propsFromState.model.id}`),
        getLineManagers: () => propsFromDispatch.dispatchGetLineManagers(propsFromState.model.id),
        getHrRepresentatives: () => propsFromDispatch.dispatchGetHrRepresentatives(propsFromState.model.id)
    };
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ManageAssignedUsersModal);
